import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

const calcTimeLeft = (fechaFin: string): TimeLeft => {
  const diff = new Date(fechaFin).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: false,
  };
};

interface BidPanelProps {
  producto: any;
  onBidSuccess?: () => void;
}

const BidPanel = ({ producto, onBidSuccess }: BidPanelProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft(producto.fecha_fin_subasta));
  const [pujaActual, setPujaActual] = useState<number>(producto.puja_actual || producto.precio_inicial || producto.precio);
  const [pujaCount, setPujaCount] = useState<number>(producto.puja_count || 0);
  const [miPuja, setMiPuja] = useState('');
  const [loading, setLoading] = useState(false);
  const [reservaLoading, setReservaLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [historial, setHistorial] = useState<any[]>([]);
  const [showHistorial, setShowHistorial] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [ganadorId, setGanadorId] = useState<string | null>(producto.ganador_id || null);
  const [estadoSubasta, setEstadoSubasta] = useState<string>(producto.estado_subasta || 'activa');
  const [miUltimaBid, setMiUltimaBid] = useState<any>(null);
  const [showContactoModal, setShowContactoModal] = useState(false);
  const [vendedorInfo, setVendedorInfo] = useState<any>(null);
  const notifTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isRealEstate = producto.categoria === 'bienes_raices';
  const isAltoValor = producto.categoria === 'autos' || isRealEstate;
  const montoReserva = isRealEstate ? 250 : 100;
  const minPuja = pujaActual + (isRealEstate ? 100 : 1);

  const soyGanador = user && ganadorId === user.id;
  const subastaTerminada = timeLeft.expired || estadoSubasta === 'cerrada_con_ganador' || estadoSubasta === 'cerrada_sin_ganador';

  // Fetch vendedor info when modal opens
  useEffect(() => {
    if (showContactoModal && producto.vendedor_id) {
      const fetchVendedor = async () => {
        const { data } = await supabase
          .from('profiles')
          .select('nombre_completo, telefono, email')
          .eq('id', producto.vendedor_id)
          .maybeSingle();
        if (data) setVendedorInfo(data);
      };
      fetchVendedor();
    }
  }, [showContactoModal, producto.vendedor_id]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(producto.fecha_fin_subasta)), 1000);
    return () => clearInterval(timer);
  }, [producto.fecha_fin_subasta]);

  const fetchBids = useCallback(async () => {
    const { data } = await supabase
      .from('bids')
      .select('*')
      .eq('producto_id', producto.id)
      .order('monto', { ascending: false })
      .limit(10);
    if (data && data.length > 0) {
      setHistorial(data);
      setPujaActual(data[0].monto);
      setPujaCount(data.length);
      if (user) {
        const misBids = data.filter(b => b.pujador_id === user.id);
        if (misBids.length > 0) setMiUltimaBid(misBids[0]);
      }
    }
  }, [producto.id, user]);

  useEffect(() => {
    fetchBids();
  }, [fetchBids]);

  // Realtime: escuchar nuevas pujas
  useEffect(() => {
    const channel = supabase
      .channel(`bids-producto-${producto.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bids', filter: `producto_id=eq.${producto.id}` },
        (payload) => {
          const nuevaPuja = payload.new as any;
          setPujaActual(prev => {
            if (nuevaPuja.monto > prev) {
              // Si el usuario tenía la puja más alta y alguien lo superó
              if (user && miUltimaBid && miUltimaBid.pujador_id === user.id && nuevaPuja.pujador_id !== user.id) {
                showNotification('¡Te han superado la puja! Alguien pujó $' + nuevaPuja.monto.toLocaleString());
              }
              return nuevaPuja.monto;
            }
            return prev;
          });
          setPujaCount(prev => prev + 1);
          fetchBids();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [producto.id, user, miUltimaBid, fetchBids]);

  // Realtime: escuchar cambios en el producto (cierre de subasta)
  useEffect(() => {
    const channel = supabase
      .channel(`producto-subasta-${producto.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'products', filter: `id=eq.${producto.id}` },
        (payload) => {
          const updated = payload.new as any;
          if (updated.ganador_id) setGanadorId(updated.ganador_id);
          if (updated.estado_subasta) setEstadoSubasta(updated.estado_subasta);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [producto.id]);

  const showNotification = (msg: string) => {
    setNotification(msg);
    if (notifTimeoutRef.current) clearTimeout(notifTimeoutRef.current);
    notifTimeoutRef.current = setTimeout(() => setNotification(null), 6000);
  };

  const handlePujar = async () => {
    if (!user) return;
    const monto = parseFloat(miPuja);
    if (isNaN(monto) || monto < minPuja) {
      setError(`La puja mínima es $${minPuja.toLocaleString()}`);
      return;
    }

    setLoading(true);
    setError('');

    const { error: bidError } = await supabase.from('bids').insert({
      producto_id: producto.id,
      pujador_id: user.id,
      monto,
    });

    if (bidError) {
      setError('Error al registrar la puja. Intenta de nuevo.');
      setLoading(false);
      return;
    }

    await supabase
      .from('products')
      .update({ puja_actual: monto, puja_count: pujaCount + 1 })
      .eq('id', producto.id);

    setPujaActual(monto);
    setPujaCount(prev => prev + 1);
    setMiPuja('');
    setSuccess(`¡Puja de $${monto.toLocaleString()} registrada!`);
    setTimeout(() => setSuccess(''), 4000);
    fetchBids();
    onBidSuccess?.();
    setLoading(false);
  };

  const handlePagarReserva = async () => {
    if (!user || !soyGanador) return;
    setReservaLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        'https://munavzcdszgqcrburqbi.supabase.co/functions/v1/create-auction-reservation',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            producto_id: producto.id,
            bid_id: miUltimaBid?.id,
          }),
        }
      );
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Error al crear el checkout');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setReservaLoading(false);
    }
  };

  const isUrgent = !timeLeft.expired && timeLeft.days === 0 && timeLeft.hours < 2;

  return (
    <div className="bg-white rounded-2xl border-2 border-amber-200 overflow-hidden relative">
      {/* Notificación realtime */}
      {notification && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-red-500 text-white px-4 py-3 flex items-center gap-3 animate-pulse">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-alarm-warning-line text-xl"></i>
          </div>
          <p className="text-sm font-bold flex-1">{notification}</p>
          <button onClick={() => setNotification(null)} className="cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-auction-line text-2xl text-white"></i>
            </div>
            <span className="text-white font-bold text-lg">Subasta en Vivo</span>
          </div>
          <span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
            {pujaCount} pujas
          </span>
        </div>

        {/* Countdown */}
        {subastaTerminada ? (
          <div className="bg-red-500/30 border border-red-300/40 rounded-xl p-3 text-center">
            <p className="text-white font-bold text-lg">Subasta Cerrada</p>
          </div>
        ) : (
          <div className={`rounded-xl p-3 ${isUrgent ? 'bg-red-500/40 border-2 border-red-400 animate-pulse' : 'bg-white/15'}`}>
            <p className="text-white/80 text-xs font-medium mb-2 text-center">Cierra en:</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { val: timeLeft.days, label: 'Días' },
                { val: timeLeft.hours, label: 'Horas' },
                { val: timeLeft.minutes, label: 'Min' },
                { val: timeLeft.seconds, label: 'Seg' },
              ].map(unit => (
                <div key={unit.label} className={`text-center rounded-lg py-2 ${isUrgent ? 'bg-red-600/60' : 'bg-white/20'}`}>
                  <p className={`text-2xl font-extrabold text-white ${isUrgent ? 'animate-pulse' : ''}`}>
                    {String(unit.val).padStart(2, '0')}
                  </p>
                  <p className="text-white/70 text-xs">{unit.label}</p>
                </div>
              ))}
            </div>
            {isUrgent && (
              <p className="text-center text-white text-xs font-bold mt-2 animate-pulse">
                <i className="ri-alarm-warning-line mr-1"></i>
                ¡Quedan menos de 5 minutos!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Puja actual */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-500 font-medium">Puja Actual</span>
          <span className="text-xs text-gray-400">
            Inicio: ${(producto.precio_inicial || producto.precio).toLocaleString()}
          </span>
        </div>
        <p className="text-4xl font-extrabold text-amber-600">
          ${pujaActual.toLocaleString()}
        </p>
        {producto.precio_reserva && (
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <i className="ri-lock-line"></i>
            Precio de reserva: confidencial
          </p>
        )}
      </div>

      {/* Bienes Raíces: nota de Derecho de Compra */}
      {isRealEstate && (
        <div className="mx-5 mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex gap-2">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-emerald-600"></i>
          </div>
          <p className="text-xs text-emerald-700 leading-relaxed">
            <strong>Subasta de Derecho de Compra.</strong> El ganador obtiene el derecho exclusivo de comprar esta propiedad al precio pujado. Mínimo de participación: <strong>$100</strong>.
          </p>
        </div>
      )}

      {/* Panel principal */}
      <div className="p-5">
        {/* ── GANADOR: Panel de victoria ── */}
        {subastaTerminada && soyGanador && (
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-2xl p-5 text-center">
            <div className="text-5xl mb-3">🏆</div>
            <h3 className="text-xl font-extrabold text-amber-800 mb-1">¡Ganaste la Subasta!</h3>
            <p className="text-amber-700 text-sm mb-2">Tu puja ganadora:</p>
            <p className="text-3xl font-extrabold text-amber-600 mb-4">${pujaActual.toLocaleString()}</p>

            {isAltoValor ? (
              <>
                <div className="bg-white border border-amber-200 rounded-xl p-4 mb-4 text-left">
                  <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-amber-500"></i>
                    Próximo Paso: Contacto Directo
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    El vendedor recibirá tus datos de contacto. Coordinarás directamente con él para finalizar la compra. Sin comisiones de plataforma.
                  </p>
                </div>
                <button
                  onClick={() => setShowContactoModal(true)}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-extrabold rounded-xl transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base"
                >
                  <i className="ri-phone-line text-xl"></i>
                  Ver Datos del Vendedor
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate(`/ganaste?producto_id=${producto.id}&bid_id=${miUltimaBid?.id}`)}
                className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-extrabold rounded-xl transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base"
              >
                <i className="ri-trophy-line text-xl"></i>
                Ver Detalles y Coordinar Entrega
              </button>
            )}
          </div>
        )}

        {/* ── SUBASTA CERRADA: no ganó ── */}
        {subastaTerminada && !soyGanador && estadoSubasta === 'cerrada_con_ganador' && (
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-close-circle-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-700 font-bold mb-1">Subasta Finalizada</p>
            <p className="text-gray-500 text-sm mb-4">Esta subasta ya tiene un ganador.</p>
            <Link to="/subastas" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white font-semibold rounded-xl text-sm whitespace-nowrap">
              <i className="ri-auction-line"></i>
              Ver Más Subastas
            </Link>
          </div>
        )}

        {/* ── SUBASTA CERRADA: sin ganador ── */}
        {subastaTerminada && estadoSubasta === 'cerrada_sin_ganador' && (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm">Esta subasta cerró sin pujas.</p>
          </div>
        )}

        {/* ── SUBASTA ACTIVA ── */}
        {!subastaTerminada && (
          <>
            {!user ? (
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-4">Inicia sesión para participar en la subasta</p>
                <div className="flex gap-3">
                  <Link to="/login" className="flex-1 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 transition-colors text-sm text-center whitespace-nowrap">
                    Iniciar Sesión
                  </Link>
                  <Link to="/registro" className="flex-1 py-3 border-2 border-amber-500 text-amber-600 font-bold rounded-xl hover:bg-amber-50 transition-colors text-sm text-center whitespace-nowrap">
                    Registrarse
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                    <p className="text-green-700 text-sm font-semibold">{success}</p>
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                    <i className="ri-error-warning-fill text-red-500 text-lg"></i>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Mi puja actual */}
                {miUltimaBid && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center justify-between">
                    <span className="text-xs text-blue-700 font-semibold flex items-center gap-1.5">
                      <i className="ri-user-line"></i>
                      Tu puja actual
                    </span>
                    <span className="text-sm font-extrabold text-blue-800">${miUltimaBid.monto.toLocaleString()}</span>
                  </div>
                )}

                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Tu Puja (mínimo ${minPuja.toLocaleString()})
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg">$</span>
                    <input
                      type="number"
                      value={miPuja}
                      onChange={e => { setMiPuja(e.target.value); setError(''); }}
                      placeholder={minPuja.toLocaleString()}
                      min={minPuja}
                      step={isRealEstate ? 100 : 1}
                      className="w-full pl-8 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-400 text-lg font-bold text-gray-900"
                    />
                  </div>
                </div>

                {/* Quick bid buttons */}
                <div className="flex gap-2 mb-4">
                  {(isRealEstate ? [500, 1000, 5000] : [5, 10, 25]).map(inc => (
                    <button
                      key={inc}
                      onClick={() => setMiPuja(String(pujaActual + inc))}
                      className="flex-1 py-2 text-xs font-bold border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      +${inc.toLocaleString()}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handlePujar}
                  disabled={loading || !miPuja}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <i className="ri-auction-line text-xl"></i>
                      Pujar Ahora
                    </>
                  )}
                </button>
              </>
            )}
          </>
        )}

        {/* Historial de pujas */}
        {historial.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowHistorial(!showHistorial)}
              className="w-full flex items-center justify-between text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors cursor-pointer py-2"
            >
              <span>Historial de Pujas ({historial.length})</span>
              {showHistorial ? <i className="ri-arrow-up-s-line text-lg"></i> : <i className="ri-arrow-down-s-line text-lg"></i>}
            </button>
            {showHistorial && (
              <div className="space-y-2 mt-2">
                {historial.map((bid, i) => (
                  <div key={bid.id} className={`flex items-center justify-between p-2.5 rounded-lg text-xs ${i === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {i + 1}
                      </div>
                      <span className="text-gray-500">
                        {new Date(bid.created_at).toLocaleDateString('es-PR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <span className={`font-bold ${i === 0 ? 'text-amber-600' : 'text-gray-700'}`}>
                      ${bid.monto.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Contacto del Vendedor */}
      {showContactoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-phone-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">¡Felicidades Ganador!</h3>
              <p className="text-sm text-gray-500">Contacta directamente con el vendedor</p>
            </div>

            {vendedorInfo ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Vendedor</p>
                  <p className="font-bold text-gray-900">{vendedorInfo.nombre_completo || 'Vendedor'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Teléfono</p>
                  <a href={`tel:${vendedorInfo.telefono}`} className="font-bold text-amber-600 text-lg flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    {vendedorInfo.telefono || 'No disponible'}
                  </a>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-900 text-sm">{vendedorInfo.email || 'No disponible'}</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                  <i className="ri-information-line text-amber-600 flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-700">
                    Sin comisiones de plataforma. Coordinarás directamente con el vendedor para finalizar la compra.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="w-8 h-8 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Cargando información...</p>
              </div>
            )}

            <button
              onClick={() => setShowContactoModal(false)}
              className="w-full mt-5 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidPanel;
